// src/pages/CustomerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { customerService } from '../services/customerService';
import { calendarService } from '../services/calendarService';
import { proposalService } from '../services/proposalService';
import { useRealtimeSubscription } from '../hooks/useRealtimeSubscription';
import toast from 'react-hot-toast';
import {
  Calendar,
  LogOut,
  Building2,
  Mail,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import ProposalCard from '../components/ProposalCard';
import LoadingSpinner, { LoadingCard } from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ConsentManagement from '../components/ConsentManagement';
import CalendarView from '../components/CalendarView';
import CalendarConnectionsPanel from '../components/CalendarConnectionsPanel';
import AppleConnectModal from '../components/AppleConnectModal';
import ChatButton from '../components/ai/ChatButton';
import ChatDrawer from '../components/ai/ChatDrawer';
import styles from './CustomerDashboard.module.css';

export default function CustomerDashboard() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const [calendarConnections, setCalendarConnections] = useState([]);
  const [calendarConnection, setCalendarConnection] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [stats, setStats] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [businesses, setBusinesses] = useState([]);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingProvider, setSyncingProvider] = useState(null);
  // TODO(naming) primary tab key — was 'offers' in legacy UI, now 'activity'
  const VALID_TABS = new Set(['activity', 'businesses', 'settings']);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab = VALID_TABS.has(rawTab) ? rawTab : 'activity';
  const setActiveTab = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      tab === 'activity' ? next.delete('tab') : next.set('tab', tab);
      return next;
    }, { replace: true });
  };
  const [error, setError] = useState(null);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [appleModalOpen, setAppleModalOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const microsoftConnected = params.get('microsoft_connected');
    const microsoftError = params.get('microsoft_error');
    const googleConnected = params.get('google_connected');
    const googleError = params.get('google_error');

    const clearConnectionParams = () => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        for (const key of ['microsoft_connected', 'microsoft_error', 'google_connected', 'google_error']) {
          next.delete(key);
        }
        return next;
      }, { replace: true });
    };

    // Wave 4: legacy /dashboard?offer=<id> deep-links bounce to home with a toast.
    if (params.get('offer')) {
      // TODO(naming) legacy offer-link redirect message
      toast('Offers have been replaced — view your activity.');
      navigate('/', { replace: true });
      return;
    }

    if (microsoftConnected === 'true') {
      toast.success('Microsoft 365 calendar connected successfully!');
      clearConnectionParams();
    } else if (microsoftError) {
      toast.error(`Failed to connect Microsoft 365: ${microsoftError}`);
      clearConnectionParams();
    } else if (googleConnected === 'true') {
      toast.success('Google Calendar connected successfully!');
      clearConnectionParams();
    } else if (googleError) {
      toast.error(`Failed to connect Google Calendar: ${googleError}`);
      clearConnectionParams();
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const ctrl = { cancelled: false };
    loadDashboardData(ctrl);
    return () => { ctrl.cancelled = true; };
  }, [user?.id]);

  // Wave 4: subscribe to the proposals table. Participant membership lives in
  // a join table (proposal_participants), so a server-side filter on
  // proposals alone can't constrain to "this user's proposals". RLS already
  // restricts what they can see, and we client-side-filter in onInsert as a
  // belt-and-braces guard. On any change we re-fetch the full list to keep
  // status counters accurate without re-implementing them in the handler.
  useRealtimeSubscription(`customer-proposals-${user?.id}`, 'proposals', {
    enabled: !!user,
    onInsert: () => {
      if (user) proposalService.list().then(r => setProposals(r.data?.proposals || r.data || []));
    },
    onUpdate: () => {
      if (user) proposalService.list().then(r => setProposals(r.data?.proposals || r.data || []));
    },
    onDelete: () => {
      if (user) proposalService.list().then(r => setProposals(r.data?.proposals || r.data || []));
    }
  });

  // Two parallel subscriptions so B2B flip-side INSERTs surface live. A single
  // row matches at most one of the two filters (CHECK constraint enforces
  // business_id != customer_id), so no double-fetch storm. Mirrors the
  // BusinessDashboard pattern landed in PR #170.
  const refreshBusinesses = () => {
    if (!user) return;
    customerService.getOptedInBusinesses(user.id).then(r => setBusinesses(r.data || []));
  };

  useRealtimeSubscription(`customer-conn-in-${user?.id}`, 'business_customer_connections', {
    filter: `customer_id=eq.${user?.id}`,
    enabled: !!user,
    onInsert: refreshBusinesses,
    onUpdate: refreshBusinesses,
  });

  useRealtimeSubscription(`customer-conn-out-${user?.id}`, 'business_customer_connections', {
    filter: `business_id=eq.${user?.id}`,
    enabled: !!user,
    onInsert: refreshBusinesses,
    onUpdate: refreshBusinesses,
  });

  const loadDashboardData = async (ctrl = { cancelled: false }) => {
    try {
      setLoading(true);
      setError(null);

      const [
        calendarResult,
        prefsResult,
        proposalsResult,
        businessesResult
      ] = await Promise.all([
        calendarService.getCalendarConnections(user.id),
        customerService.getCustomerPreferences(user.id),
        proposalService.list(),
        customerService.getOptedInBusinesses(user.id)
      ]);

      if (ctrl.cancelled) return;

      const proposalList = proposalsResult.data?.proposals || proposalsResult.data || [];
      const statsResult = await customerService.getDashboardStats(user.id, proposalList);

      if (ctrl.cancelled) return;

      setCalendarConnections(calendarResult);
      setCalendarConnection(calendarResult[0] || null);
      setPreferences(prefsResult.data);
      setStats(statsResult.data);
      setProposals(proposalList);
      setBusinesses(businessesResult.data || []);
    } catch (err) {
      if (ctrl.cancelled) return;
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      if (!ctrl.cancelled) setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    window.location.href = await calendarService.getGoogleAuthUrl();
  };

  const handleSyncProvider = async (provider) => {
    try {
      setSyncingProvider(provider);
      setError(null);

      const connection = calendarConnections.find(c => c.provider === provider);
      if (!connection) throw new Error(`No ${provider} connection found`);

      const result = await calendarService.syncAvailability(user.id, connection);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections);
      setCalendarConnection(updatedConnections[0] || null);
      setCalendarRefreshKey(prev => prev + 1);

      const calendarText = result.calendarsCount === 1 ? 'calendar' : 'calendars';
      const providerName = provider === 'google' ? 'Google' : 'Microsoft 365';
      toast.success(`${providerName}: Synced ${result.calendarsCount} ${calendarText} with ${result.blocksCount} busy blocks!`);
    } catch (err) {
      console.error(`Error syncing ${provider} calendar:`, err);
      setError(`Failed to sync ${provider} calendar`);
      toast.error(`Failed to sync ${provider} calendar`);
    } finally {
      setSyncingProvider(null);
    }
  };

  const handleSyncAllProviders = async () => {
    try {
      setSyncing(true);
      setError(null);

      if (calendarConnections.length === 0) throw new Error('No calendar connections found');

      const result = await calendarService.syncAllProviders(user.id);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections);
      setCalendarConnection(updatedConnections[0] || null);
      setCalendarRefreshKey(prev => prev + 1);

      if (result.successCount > 0) {
        toast.success(`Successfully synced ${result.successCount} provider(s): ${result.totalCalendars} calendars, ${result.totalBlocks} busy blocks!`);
      }
      if (result.failureCount > 0) {
        toast.error(`Failed to sync ${result.failureCount} provider(s)`);
      }
    } catch (err) {
      console.error('Error syncing calendars:', err);
      setError('Failed to sync calendars');
      toast.error('Failed to sync calendars');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncNow = async () => {
    if (calendarConnections.length > 1) {
      await handleSyncAllProviders();
    } else if (calendarConnections.length === 1) {
      await handleSyncProvider(calendarConnections[0].provider);
    } else {
      toast.error('No calendar connections found');
    }
  };

  const handleDisconnectProvider = async (provider) => {
    const labels = { google: 'Google Calendar', microsoft: 'Microsoft 365 Calendar', apple: 'Apple Calendar' };
    const providerName = labels[provider] || provider;
    if (!confirm(`Are you sure you want to disconnect ${providerName}?`)) return;

    try {
      await calendarService.disconnectCalendar(user.id, provider);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections);
      setCalendarConnection(updatedConnections[0] || null);
      setCalendarRefreshKey(prev => prev + 1);
      toast.success(`${providerName} disconnected successfully`);
      // C4: the app-specific password stays live in the user's Apple ID until they remove it.
      if (provider === 'apple') {
        toast('Remember to delete the Vennio app-specific password in your Apple ID settings (appleid.apple.com).', { duration: 8000, icon: 'ℹ️' });
      }
    } catch (err) {
      console.error(`Error disconnecting ${provider} calendar:`, err);
      setError(`Failed to disconnect ${provider} calendar`);
      toast.error(`Failed to disconnect ${provider} calendar`);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!confirm('Are you sure you want to disconnect all calendars?')) return;

    try {
      await calendarService.disconnectCalendar(user.id);
      setCalendarConnections([]);
      setCalendarConnection(null);
      setCalendarRefreshKey(prev => prev + 1);
      toast.success('All calendars disconnected successfully');
    } catch (err) {
      console.error('Error disconnecting calendars:', err);
      setError('Failed to disconnect calendars');
      toast.error('Failed to disconnect calendars');
    }
  };

  const refreshProposals = async () => {
    const { data } = await proposalService.list();
    const list = data?.proposals || data || [];
    setProposals(list);
    const statsResult = await customerService.getDashboardStats(user.id, list);
    setStats(statsResult.data);
  };

  const handleAcceptProposal = async (proposalId, payload = {}) => {
    setError(null);
    const { error: respError } = await proposalService.respond({
      id: proposalId,
      action: 'accept',
      selectedSlotId: payload.selectedSlotId,
    });
    if (respError) {
      // TODO(naming) accept failure toast
      toast.error('Could not accept proposal.');
      setError('Failed to accept proposal.');
      return;
    }
    // TODO(naming) accept success toast
    toast.success('Proposal accepted.');
    refreshProposals();
  };

  const handleDeclineProposal = async (proposalId) => {
    setError(null);
    const { error: respError } = await proposalService.respond({
      id: proposalId,
      action: 'reject',
    });
    if (respError) {
      // TODO(naming) decline failure toast
      toast.error('Could not decline proposal.');
      return;
    }
    // TODO(naming) decline success toast
    toast.success('Proposal declined.');
    refreshProposals();
  };

  const handleCancelProposal = async (proposalId) => {
    setError(null);
    const { error: cancelError } = await proposalService.cancel(proposalId);
    if (cancelError) {
      // TODO(naming) cancel failure toast
      toast.error('Could not cancel meeting.');
      return;
    }
    // TODO(naming) cancel success toast
    toast.success('Meeting cancelled.');
    refreshProposals();
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const formData = new FormData(e.target);
      const prefsData = {
        notification_enabled: formData.get('notifications') === 'on',
        notification_frequency: formData.get('frequency'),
        preferred_time_start: formData.get('time_start'),
        preferred_time_end: formData.get('time_end'),
        availability_window_days: parseInt(formData.get('window_days')) || 14
      };
      const { error } = await customerService.updateCustomerPreferences(user.id, prefsData);
      if (error) throw error;
      setPreferences(prefsData);
      alert('Preferences saved successfully!');
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences');
    }
  };

  const handleOptOut = async (connectionId) => {
    if (!confirm('Are you sure you want to disconnect from this business?')) return;

    try {
      setError(null);
      const { error } = await customerService.disconnectFromBusiness(connectionId);
      if (error) throw error;
      const businessesResult = await customerService.getOptedInBusinesses(user.id);
      setBusinesses(businessesResult.data || []);
      alert('Disconnected');
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect from business');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  const pendingProposals = proposals.filter(p => p.status === 'proposed');
  const unhealthyConnections = (calendarConnections || []).filter(c => c.health && c.health !== 'ok');
  const handleConnectMicrosoft = async () => {
    window.location.href = await calendarService.getMicrosoftAuthUrl();
  };

  const handleConnectApple = () => setAppleModalOpen(true);

  // Apple connect is a direct POST (no OAuth redirect), so refresh in-place.
  const handleAppleConnected = async () => {
    const updatedConnections = await calendarService.getCalendarConnections(user.id);
    setCalendarConnections(updatedConnections || []);
    setCalendarConnection(updatedConnections?.[0] || null);
    setCalendarRefreshKey(prev => prev + 1);
  };

  // Build syncingProviders map for CalendarConnectionsPanel compatibility
  const syncingProviders = syncingProvider ? { [syncingProvider]: true } : {};

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <div className={styles.logoWrap}>
              <Calendar className={styles.logoIcon} size={24} />
            </div>
            <div>
              <h1 className={styles.headerTitle}>Vennio</h1>
              <p className={styles.headerSub}>Customer Dashboard</p>
            </div>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.headerEmail}>{profile?.email}</span>
            <button onClick={() => signOut()} className={styles.signOutBtn}>
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className={styles.main}>
        {/* Error */}
        {error && (
          <div className={styles.errorBox}>
            <XCircle size={20} className={styles.errorIcon} />
            <div>
              <p className={styles.errorTitle}>Error</p>
              <p className={styles.errorText}>{error}</p>
            </div>
          </div>
        )}

        {/* Compact status bar */}
        <div className={styles.statusBar}>
          <span className={styles.statusItem}>
            <Mail size={14} />
            <strong>{stats?.pendingOffers || 0}</strong> pending offer{(stats?.pendingOffers || 0) !== 1 ? 's' : ''}
          </span>
          <span className={styles.statusDot} />
          <span className={styles.statusItem}>
            <Calendar size={14} />
            <strong>{stats?.upcomingAppointments || 0}</strong> upcoming
          </span>
          <span className={styles.statusDot} />
          <span className={styles.statusItem}>
            <Building2 size={14} />
            <strong>{stats?.optedInBusinesses || 0}</strong> business{(stats?.optedInBusinesses || 0) !== 1 ? 'es' : ''}
          </span>
        </div>

        {/* Calendar banner — only if no calendar or unhealthy */}
        {calendarConnections.length === 0 && (
          <div className={styles.calendarBanner}>
            <Calendar size={18} className={styles.bannerIcon} />
            <p className={styles.bannerText}>Connect your calendar to receive appointment offers</p>
            <div className={styles.bannerActions}>
              <button onClick={handleConnectCalendar} className={styles.bannerBtn}>
                Connect Google
              </button>
              <button onClick={handleConnectMicrosoft} className={styles.bannerBtn}>
                Connect Microsoft
              </button>
            </div>
          </div>
        )}
        {unhealthyConnections.map(conn => (
          <div key={conn.provider} className={styles.calendarBannerWarning}>
            <AlertTriangle size={18} />
            <p className={styles.bannerText}>
              {conn.provider === 'google' ? 'Google Calendar' : 'Microsoft 365'}{' — '}
              {conn.token_status?.message || 'Attention needed'}
            </p>
          </div>
        ))}

        {/* Tabs — primary content */}
        <div className={styles.tabsCard}>
          <nav className={styles.tabNav}>
            {[
              // TODO(naming) primary tab label
              { id: 'activity',   label: `Activity${pendingProposals.length > 0 ? ` (${pendingProposals.length})` : ''}` },
              { id: 'businesses', label: 'My Businesses' },
              { id: 'settings',   label: 'Settings' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className={styles.tabBody}>
            {/* Activity (was Offers) */}
            {activeTab === 'activity' && (
              <div>
                {proposals.length === 0 ? (
                  <EmptyState
                    icon={Mail}
                    /* TODO(naming) empty-state title */
                    title="No activity yet"
                    /* TODO(naming) empty-state description */
                    description="When someone sends you a meeting proposal, it'll show up here."
                  />
                ) : (
                  <div className={styles.offerList}>
                    {proposals.map(proposal => (
                      <ProposalCard
                        key={proposal.id}
                        proposal={proposal}
                        viewerId={user.id}
                        onAccept={handleAcceptProposal}
                        onDecline={handleDeclineProposal}
                        onCancel={handleCancelProposal}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My Businesses */}
            {activeTab === 'businesses' && (
              <div>
                {businesses.length === 0 ? (
                  <EmptyState
                    icon={Building2}
                    title="No connected businesses yet"
                    description="When a business invites you and you accept, it appears here."
                  />
                ) : (
                  <div className={styles.businessList}>
                    {businesses.map(conn => (
                      <div key={conn.id} className={styles.businessCard}>
                        <div className={styles.businessCardInner}>
                          <div>
                            <h3 className={styles.businessName}>
                              {conn.business?.business_profile?.[0]?.business_name || conn.business?.full_name}
                            </h3>
                            <p className={styles.businessService}>
                              {conn.business?.business_profile?.[0]?.service_type}
                            </p>
                            <p className={styles.businessMeta}>
                              Connected {new Date(conn.opted_in_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button onClick={() => handleOptOut(conn.id)} className={styles.secondaryBtn}>
                            Disconnect
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings (merged: Calendar + Preferences + Access) */}
            {activeTab === 'settings' && (
              <div className={styles.settingsSections}>
                {/* Calendar Connections */}
                <div className={styles.settingsSection}>
                  <CalendarConnectionsPanel
                    calendarConnections={calendarConnections}
                    syncingProviders={syncingProviders}
                    syncing={syncing}
                    onSyncProvider={handleSyncProvider}
                    onSyncAll={handleSyncAllProviders}
                    onConnectGoogle={handleConnectCalendar}
                    onConnectMicrosoft={handleConnectMicrosoft}
                    onConnectApple={handleConnectApple}
                    onDisconnectProvider={handleDisconnectProvider}
                    defaultExpanded
                  />
                </div>

                {/* Calendar View */}
                {calendarConnections.length > 0 && (
                  <div className={styles.settingsSection}>
                    <h3 className={styles.settingsSectionTitle}>Your Availability</h3>
                    <CalendarView userId={user.id} refreshTrigger={calendarRefreshKey} />
                  </div>
                )}

                {/* Preferences */}
                <div className={styles.settingsSection}>
                  <h3 className={styles.settingsSectionTitle}>Preferences</h3>
                  <form onSubmit={handleSavePreferences} className={styles.prefForm}>
                    <div>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          name="notifications"
                          defaultChecked={preferences?.notification_enabled !== false}
                          className={styles.prefCheckbox}
                        />
                        <span className={styles.checkboxText}>Enable notifications</span>
                      </label>
                    </div>

                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Notification frequency</label>
                      <select
                        name="frequency"
                        defaultValue={preferences?.notification_frequency || 'daily'}
                        className={styles.formSelect}
                      >
                        <option value="realtime">Real-time</option>
                        <option value="daily">Daily digest</option>
                        <option value="weekly">Weekly digest</option>
                      </select>
                    </div>

                    <div className={styles.timeGrid}>
                      <div className={styles.formField}>
                        <label className={styles.formLabel}>Preferred start time</label>
                        <input
                          type="time"
                          name="time_start"
                          defaultValue={preferences?.preferred_time_start || '09:00'}
                          className={styles.formInput}
                        />
                      </div>
                      <div className={styles.formField}>
                        <label className={styles.formLabel}>Preferred end time</label>
                        <input
                          type="time"
                          name="time_end"
                          defaultValue={preferences?.preferred_time_end || '17:00'}
                          className={styles.formInput}
                        />
                      </div>
                    </div>

                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Look ahead (days)</label>
                      <input
                        type="number"
                        name="window_days"
                        min="1"
                        max="90"
                        defaultValue={preferences?.availability_window_days || 14}
                        className={styles.formInput}
                      />
                      <p className={styles.fieldHint}>How many days ahead to check for availability</p>
                    </div>

                    <button type="submit" className={`${styles.accentBtn} ${styles.accentBtnFull}`}>
                      Save Preferences
                    </button>
                  </form>
                </div>

                {/* Shared Access */}
                <div className={styles.settingsSection}>
                  <h3 className={styles.settingsSectionTitle}>Shared Access</h3>
                  <ConsentManagement userId={user.id} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <AppleConnectModal
        isOpen={appleModalOpen}
        onClose={() => setAppleModalOpen(false)}
        onConnected={handleAppleConnected}
      />
      <ChatButton onClick={() => setChatOpen(true)} />
      <ChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        activeTab={activeTab}
        calendarConnected={calendarConnections.length > 0}
        hasBusinesses={businesses.length > 0}
      />
    </div>
  );
}
